import { create } from 'zustand';
import { format, startOfDay } from 'date-fns';
import { isPlaySeriesScheduleComplete, type PlayEvent } from '@court-sync/shared';
import { playEventService } from '../services/playEvent';
import { CACHE_TTL, isCacheValid } from './types';

interface DateRangeYmd {
  fromYmd: string;
  toYmd: string;
}

function toYmd(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

function rangeCacheKey(fromYmd: string, toYmd: string): string {
  return `${fromYmd}|${toYmd}`;
}

function playEventStartYmd(event: PlayEvent): string {
  const startAt = event.startsAt instanceof Date ? event.startsAt : new Date(event.startsAt);
  return format(startOfDay(startAt), 'yyyy-MM-dd');
}

function mergePlayEventsForRange(
  prev: PlayEvent[],
  incoming: PlayEvent[],
  fromYmd: string,
  toYmd: string
): PlayEvent[] {
  const outside = prev.filter((event) => {
    const ymd = playEventStartYmd(event);
    return ymd < fromYmd || ymd > toYmd;
  });
  return [...outside, ...incoming];
}

function filterPlayEventsToRange(
  events: PlayEvent[],
  fromYmd: string,
  toYmd: string
): PlayEvent[] {
  return events.filter((event) => {
    const ymd = playEventStartYmd(event);
    return ymd >= fromYmd && ymd <= toYmd;
  });
}

function unionRanges(ranges: DateRangeYmd[]): DateRangeYmd | null {
  if (ranges.length === 0) return null;
  let fromYmd = ranges[0]!.fromYmd;
  let toYmdVal = ranges[0]!.toYmd;
  for (const range of ranges.slice(1)) {
    if (range.fromYmd < fromYmd) fromYmd = range.fromYmd;
    if (range.toYmd > toYmdVal) toYmdVal = range.toYmd;
  }
  return { fromYmd, toYmd: toYmdVal };
}

async function ensurePlaySeriesSessionsGenerated(organizationId: string): Promise<void> {
  const seriesRes = await playEventService.listSeries(organizationId);
  if (!seriesRes.success || !seriesRes.data) return;

  for (const series of seriesRes.data) {
    if (!series.isActive || !isPlaySeriesScheduleComplete(series)) continue;
    try {
      await playEventService.generateSeriesUpcoming(organizationId, series.id);
    } catch {
      // Best-effort: court conflicts or ended schedules should not block the calendar.
    }
  }
}

interface PlayEventCalendarState {
  organizationId: string | null;
  playEvents: PlayEvent[];
  loadedRanges: DateRangeYmd[];
  rangeLastFetchedAt: Record<string, number>;
  seriesBootstrapCompletedOrgs: Set<string>;
  fetchError: string | null;

  resetForOrganization: (organizationId: string) => void;
  clear: () => void;
  fetchForRange: (
    organizationId: string,
    start: Date,
    end: Date,
    options?: { force?: boolean }
  ) => Promise<PlayEvent[]>;
  refreshAfterMutation: (organizationId: string) => Promise<PlayEvent[]>;
}

const initialState = {
  organizationId: null as string | null,
  playEvents: [] as PlayEvent[],
  loadedRanges: [] as DateRangeYmd[],
  rangeLastFetchedAt: {} as Record<string, number>,
  seriesBootstrapCompletedOrgs: new Set<string>(),
  fetchError: null as string | null,
};

export const usePlayEventCalendarStore = create<PlayEventCalendarState>((set, get) => ({
  ...initialState,

  resetForOrganization: (organizationId) => {
    const { organizationId: currentOrganizationId } = get();
    if (currentOrganizationId === organizationId) return;
    set({
      ...initialState,
      organizationId,
      seriesBootstrapCompletedOrgs: new Set<string>(),
    });
  },

  clear: () => {
    set({
      ...initialState,
      seriesBootstrapCompletedOrgs: new Set<string>(),
    });
  },

  fetchForRange: async (organizationId, start, end, options) => {
    const fromYmd = toYmd(start);
    const endYmd = toYmd(end);
    const cacheKey = rangeCacheKey(fromYmd, endYmd);

    if (get().organizationId !== organizationId) {
      get().resetForOrganization(organizationId);
    }

    const rangeAlreadyLoaded = get().loadedRanges.some(
      (range) => range.fromYmd === fromYmd && range.toYmd === endYmd
    );
    const rangeFetchedAt = get().rangeLastFetchedAt[cacheKey];
    const rangeCacheFresh =
      !options?.force &&
      rangeAlreadyLoaded &&
      get().organizationId === organizationId &&
      isCacheValid(rangeFetchedAt, CACHE_TTL.MEDIUM);

    if (rangeCacheFresh) {
      return filterPlayEventsToRange(get().playEvents, fromYmd, endYmd);
    }

    try {
      if (!get().seriesBootstrapCompletedOrgs.has(organizationId)) {
        await ensurePlaySeriesSessionsGenerated(organizationId);
        set((state) => ({
          seriesBootstrapCompletedOrgs: new Set(state.seriesBootstrapCompletedOrgs).add(
            organizationId
          ),
        }));
      }

      const from = start.toISOString();
      const to = end.toISOString();
      const playEventsRes = await playEventService.listEvents(organizationId, {
        from,
        to,
        includeSeriesEvents: true,
      });
      const incoming =
        playEventsRes.success && playEventsRes.data ? playEventsRes.data : [];

      set((state) => {
        const nextLoadedRanges = [...state.loadedRanges];
        const alreadyLoaded = nextLoadedRanges.some(
          (range) => range.fromYmd === fromYmd && range.toYmd === endYmd
        );
        if (!alreadyLoaded) {
          nextLoadedRanges.push({ fromYmd, toYmd: endYmd });
        }
        const merged = mergePlayEventsForRange(state.playEvents, incoming, fromYmd, endYmd);
        return {
          organizationId,
          playEvents: merged,
          loadedRanges: nextLoadedRanges,
          rangeLastFetchedAt: { ...state.rangeLastFetchedAt, [cacheKey]: Date.now() },
          fetchError: null,
        };
      });

      return filterPlayEventsToRange(get().playEvents, fromYmd, endYmd);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to load play events for calendar';
      set({ fetchError: message });
      return filterPlayEventsToRange(get().playEvents, fromYmd, endYmd);
    }
  },

  refreshAfterMutation: async (organizationId) => {
    const { loadedRanges, organizationId: currentOrganizationId } = get();
    if (currentOrganizationId && currentOrganizationId !== organizationId) {
      get().resetForOrganization(organizationId);
    }

    const union = unionRanges(loadedRanges);
    if (!union) {
      return [];
    }

    set({
      organizationId,
      rangeLastFetchedAt: {},
      seriesBootstrapCompletedOrgs: new Set(get().seriesBootstrapCompletedOrgs),
    });

    const start = new Date(`${union.fromYmd}T12:00:00`);
    const end = new Date(`${union.toYmd}T12:00:00`);
    return get().fetchForRange(organizationId, start, end, { force: true });
  },
}));
